import { Box } from "@mui/system";
import React, { useEffect, useState } from "react";
import {
  Route,
  Routes,
  useLocation,
  useNavigate,
  Navigate,
} from "react-router-dom";
import BusinessAccount from "./BusinessAccount/BusinessAccount";
import Reviews from "./Home/Reviews";
import MyReviews from "./Home/MyReviews";
import Plans from "./Plans/PlansComponent";
import CommunityRates from "./Home/CommunityRates";
import CommunitySpaces from "./Home/CommunitySpaces";
import TopUsers from "./Home/TopUsers";
import Invitations from "./Invitations/Invitations";

import Profile from "./Profile/Profile";
import Bookmarks from "./Bookmarks/Bookmarks";
import Login from "./Login/Login";
import TrendsAndAnalytics from "./analytics/TrendsAndAnalytics";
import WebsiteDetails from "./WebsiteDetails/WebsiteDetails";
import OthersProfile from "./Profile/OthersProfile";
import Space from "./Space/Space";
import MySpaces from "./Space/MySpaces";
import Callback from "./Callback";
import Reports from "./Reports/Reports";
import ExtensionLoginSuccess from "./Extension/ExtensionLoginSuccess";
import PointsMarket from "./Points/PointsMarket";
import EarnPoints from "./Points/EarnPoints";
import MyTickets from "./Tickets/MyTickets";
import TicketsDashboard from "./Tickets/TicketsDashboard";
import TermsPage from "./Terms/TermsPage";
import SupportPage from "./Support/SupportPage";
import InfoPage from "./Info/InfoPage";
import OrganizationReviews from "./Organization/OrganizationReviews";
import OrganizationCollections from "./Organization/OrganizationCollections";
import OrganizationMyReviews from "./Organization/OrganizationMyReviews";
import SharedCollections from "./SharedCollections";
import MyCollections from "./MyCollections";
import SubscribedCollections from "./SubscribedCollections";
import OrganizationInvitations from "./Organization/OrganizationInvitations";
import OrganizationManagement from "./Organization/OrganizationManagement";
import Settings from "./Settings/Settings";
import { useDispatch } from "react-redux";
import axios from "axios";
import { API_URL } from "../consts";
import { Auth, Hub } from "aws-amplify";
import { storeProfileSafely, signOut, cleanupUnnecessaryCookies } from "../utils/authUtils";
import { getSafeRedirectUrl } from "../utils/redirectValidation";

const Router = ({ authorized, setIsLocationAuth, user }) => {
  const dispatch = useDispatch();
  const location = useLocation();
  let navigate = useNavigate();
  // SECURITY: Check authentication from Amplify session, not localStorage
  const [userAuthorized, setUserAuthorized] = useState(false);

  const sendTokensToExtension = (accessToken, refreshToken) => {
    // Note: Using window.location.origin for security. Extension should verify origin.
    // For browser extensions, consider using chrome.runtime.sendMessage instead of postMessage.
    window.postMessage(
      {
        action: "sendTokens",
        accessToken: accessToken,
        refreshToken: refreshToken,
        source: "datarate-web",
        timestamp: Date.now()
      },
      window.location.origin
    );
  };

  // Improved refresh token function
  const refreshAccessToken = async () => {
    try {
      // Get current session and refresh it
      const currentUser = await Auth.currentAuthenticatedUser();
      const session = await Auth.currentSession();
      
      // Force refresh the session
      const refreshedSession = await Auth.currentSession({ bypassCache: true });
      
      const newAccessToken = refreshedSession.getAccessToken().getJwtToken();
      const refreshToken = refreshedSession.getRefreshToken().getToken();

      // SECURITY: Tokens are now in httpOnly cookies via Amplify
      // Cookies are sent automatically, no Authorization header needed
      
      // Send tokens to extension
      sendTokensToExtension(newAccessToken, refreshToken);
      
      // Update Redux store
      dispatch({
        type: "SET_TOKEN",
        payload: newAccessToken,
      });

      return newAccessToken;
    } catch (error) {
      console.error("Failed to refresh token:", error);
      
      // If refresh fails, sign out user and clear all cookies
      try {
        await signOut(); // This clears all Cognito cookies for all users
      } catch (signOutError) {
        console.error("Error signing out:", signOutError);
      }
      
      // Dispatch unauthorized action
      dispatch({ type: "SET_UNAUTHORIZED" });
      setUserAuthorized(false);
      
      // Don't redirect here - let the interceptor handle it to prevent loops
      throw error;
    }
  };

  // Export refresh function for use in axios interceptors immediately
  React.useEffect(() => {
    window.refreshAccessToken = refreshAccessToken;
    
    // Cleanup function
    return () => {
      window.refreshAccessToken = null;
    };
  }, []);

  // Enhanced session check with better error handling
  const checkUserSession = async () => {
    try {
      const session = await Auth.currentSession();
      const accessToken = session.getAccessToken().getJwtToken();
      const refreshToken = session.getRefreshToken().getToken();

      // Check if token is about to expire (expires in less than 5 minutes)
      const tokenExpiry = session.getAccessToken().getExpiration();
      const currentTime = Math.floor(Date.now() / 1000);
      const timeUntilExpiry = tokenExpiry - currentTime;

      if (timeUntilExpiry < 300) { // Less than 5 minutes
        await refreshAccessToken();
        return;
      }

      // SECURITY: Tokens are now in httpOnly cookies via Amplify
      // Cookies are sent automatically, no Authorization header needed
      dispatch({ type: "SET_TOKEN", payload: accessToken });
      
      sendTokensToExtension(accessToken, refreshToken);
      setUserAuthorized(true);

      // Get user profile
      getProfile(accessToken);
      
      // Handle navigation after login with validation to prevent open redirect
      // Only apply redirect when on a login/transition page, not when already navigating the app
      let urlToNavigate = localStorage.getItem('redirectAfterLogin');
      localStorage.removeItem('redirectAfterLogin');
      const isTransitionPage = ['/login', '/callback'].includes(location.pathname);
      if (urlToNavigate && isTransitionPage) {
        // Validate and sanitize redirect URL to prevent open redirect attacks
        const safeUrl = getSafeRedirectUrl(urlToNavigate, '/');
        navigate(safeUrl);

        if (safeUrl !== urlToNavigate) {
          console.warn('Invalid redirect URL blocked:', urlToNavigate, '-> redirecting to:', safeUrl);
        }
      }
      
      dispatch({
        type: "SET_USER_DATA",
        payload: { token: accessToken },
      });
    } catch (error) {
      console.error("Error retrieving session:", error);
      
      // Try to refresh token before giving up
      try {
        await refreshAccessToken();
      } catch (refreshError) {
        console.error("Session check failed and token refresh failed:", refreshError);
        setUserAuthorized(false);
      }
    }
  };

  // Setup automatic token refresh checking
  useEffect(() => {
    const setupTokenRefreshCheck = () => {
      // Check token expiry every 4 minutes
      const interval = setInterval(async () => {
        try {
          const session = await Auth.currentSession();
          const tokenExpiry = session.getAccessToken().getExpiration();
          const currentTime = Math.floor(Date.now() / 1000);
          const timeUntilExpiry = tokenExpiry - currentTime;

          // If token expires in less than 5 minutes, refresh it
          if (timeUntilExpiry < 300) {
            await refreshAccessToken();
          }
        } catch (error) {
          console.error('Error in token refresh check:', error);
          // Clear interval if user is no longer authenticated
          clearInterval(interval);
        }
      }, 240000); // Check every 4 minutes

      return interval;
    };

    let refreshInterval;
    if (userAuthorized) {
      refreshInterval = setupTokenRefreshCheck();
    }

    return () => {
      if (refreshInterval) {
        clearInterval(refreshInterval);
      }
    };
  }, [userAuthorized]);

  useEffect(() => {
    // SECURITY: Check authentication status on mount
    const checkAuthStatus = async () => {
      try {
        const { isAuthenticated } = await import('../utils/authUtils');
        const authenticated = await isAuthenticated();
        setUserAuthorized(authenticated);
      } catch (error) {
        setUserAuthorized(false);
      }
    };
    
    checkAuthStatus();
    
    // Listen for auth events to ensure the user session is properly restored
    const listener = async (data) => {
      if (data.payload.event === "signIn" || data.payload.event === "cognitoHostedUI") {
        checkUserSession(); // Call the session check when the user is signed in
        setUserAuthorized(true);
        
        // Clean up unnecessary cookies only after fresh login (not on refresh)
        // Wait a bit to ensure Amplify has finished setting cookies
        setTimeout(() => {
          cleanupUnnecessaryCookies().catch(err => {
            console.warn('Error cleaning up cookies after login:', err);
          });
        }, 1000);
      } else if (data.payload.event === "signOut") {
        setUserAuthorized(false);
      }
    };

    Hub.listen("auth", listener);

    setIsLocationAuth(location.pathname === "/login");

    return () => {
      Hub.remove("auth", listener);
    };
  }, [location.pathname]);

  const getProfile = (token) => {
    dispatch({ type: "SET_TOKEN", payload: token });

    // SECURITY: Cookies are sent automatically, no Authorization header needed
    let url = `${API_URL}/profile`;
    axios
      .get(url, {
        withCredentials: true, // Ensure cookies are sent
      })
      .then((res) => {
        const profileData = { ...res.data.profile };

        // Store only non-sensitive profile data
        storeProfileSafely(profileData);

        dispatch({
          type: "SET_TOKEN_IN_PROCESS",
          payload: false,
        });
        dispatch({
          type: "SET_USER_DATA",
          payload: { ...res.data.profile },
        });
      })
      .catch((error) => {
        dispatch({
          type: "SET_TOKEN_IN_PROCESS",
          payload: false,
        });
        console.error("Error fetching user profile:", error);
        dispatch({
          type: "SET_UNAUTHORIZED",
        });
      });
  };

  return (
    <Box component="main" className={location.pathname === "/info" ? "" : (authorized ? "mainBox" : "mainBox2")}>
      <Routes>
      <Route path="/callback" element={<Callback/>} exact />

        <Route path="/" element={ <CommunityRates />} exact />
        <Route path="/profile" element={<Profile />} exact />
        <Route path="/trends" element={<TrendsAndAnalytics />} exact />
        <Route path="/plans" element={<Plans />} exact />
        <Route path="/settings" element={<Settings />} exact />
        <Route path="/my-reviews" element={<MyReviews />} exact />
        <Route path="/community" element={<CommunityRates />} exact />
        <Route path="/community-spaces" element={<CommunitySpaces />} exact />
        <Route path="/top-users" element={<TopUsers />} exact />
        <Route path="/points-market" element={<PointsMarket />} exact />
        <Route path="/earn-points" element={<EarnPoints />} exact />
        <Route path="/invitations" element={<Invitations />} exact />
        <Route path="/my-tickets" element={<MyTickets />} exact />
        <Route path="/tickets" element={<TicketsDashboard />} exact />
        
        {/* Organization routes */}
        <Route path="/organization" element={<OrganizationManagement />} exact />
        <Route path="/organization/reviews" element={<OrganizationReviews />} exact />
        <Route path="/organization/my-reviews" element={<OrganizationMyReviews />} exact />
        <Route path="/organization/collections" element={<OrganizationCollections />} exact />
        <Route path="/my-collections" element={<MyCollections />} exact />
        <Route path="/shared-collections" element={<SharedCollections />} exact />
        <Route path="/subscribed-collections" element={<SubscribedCollections />} exact />
        <Route path="/organization/invitations" element={<Navigate to="/organization" replace />} exact />

        <Route path="/add-website" element={<BusinessAccount />} exact />
        <Route path="/add-space" element={<BusinessAccount />} exact />
        <Route
          path="/my-websites"
          element={
            userAuthorized ? <Reports /> : <Navigate to="/add-website" />
          }
          exact
        />
        <Route path="/login" element={<Login />}
          state={{ from: location.pathname }}
          exact />
        <Route path="/my-spaces" element={<Space />} exact />
        <Route path="/website/:id" element={<WebsiteDetails />} exact />
        <Route path="/spaces/:id" element={<Space />} exact />
        <Route path="/user/:id" element={<OthersProfile />} exact />
        <Route
          path="/extension-login-success"
          element={<ExtensionLoginSuccess />}
          exact
        />
        <Route path="/terms" element={<TermsPage />} exact />
        <Route path="/support" element={<SupportPage />} exact />
        <Route path="/info" element={<InfoPage />} exact />
        {/* Catch-all: unknown URLs redirect to home instead of rendering a blank page */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Box>
  );
};

export default Router;
